import { type Ref, computed } from 'vue'
import { useUserStore } from '@/store'
import type { RolePermission } from '@/constants/role'
import {
  hasFolderPermission as rawHasFolderPermission,
  hasPermission as rawHasPermission,
  isUserRole as rawIsUserRole,
} from '@/constants/role'

/**
 * Centralized authorization helpers
 * - Prefer using this instead of importing helpers from constants/role directly
 */
export function useAuthz(userRole?: Ref<any>) {
  const userStore = useUserStore()

  // Role source: passed-in ref has priority, fallback to store
  const role = computed(() => userRole?.value ?? userStore.role)

  const isOwner = computed(() => role.value?.is_owner === true)

  const has = (permission: RolePermission) => rawHasPermission(role.value, permission)

  const hasFolder = (permission: RolePermission, folderId: number) =>
    rawHasFolderPermission(role.value, permission, folderId)

  const isRole = (...roles: string[]) => rawIsUserRole(role.value, ...roles)

  const canAny = (...perms: RolePermission[]) => isOwner.value || perms.some(p => has(p))

  const canAll = (...perms: RolePermission[]) => isOwner.value || perms.every(p => has(p))

  return {
    role,
    isOwner,
    has,
    hasFolder,
    isRole,
    canAny,
    canAll,
  }
}

export type { RolePermission } from '@/constants/role'
